import { useState } from "react";
import {
  Package, Cpu, Palette,
  Megaphone, Wrench, Lightbulb, ArrowRight,
  FileText, Cog, Truck, Users, Monitor,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";

// Commercial layer data — public-safe. All figures are qualitative ("per cycle",
// "monthly fee", "scoped project"). Any real internal fee data must stay
// server-side (see /app/backend/airline_content/*.json) and never ship in the
// public JS bundle.
const commercialLayers = {
  core: {
    id: "core",
    title: "Core CSP",
    label: "Operational Services",
    pricingHow: "usage / cycle-based",
    color: "#FFD700",
    icon: Package,
    description: "Essential CSP foundation for content operations",
    items: [
      { name: "Licensing", pricing: "per title or package", icon: FileText },
      { name: "Technical", pricing: "per title or package", icon: Cog },
      { name: "Integration", pricing: "per cycle", icon: Truck },
      { name: "Acc. Management", pricing: "per cycle", icon: Users },
    ],
  },
  platform: {
    id: "platform",
    title: "Platform",
    label: "Recurring SaaS",
    pricingHow: "monthly subscriptions",
    color: "#3B82F6",
    icon: Cpu,
    description: "Digital infrastructure and intelligence layer",
    items: [
      { name: "NucleusOS", pricing: "monthly fee", icon: Cpu },
      { name: "AI Rec. Engine", pricing: "monthly fee", icon: Lightbulb },
      { name: "IFE Platform", pricing: "per tail / per month", icon: Monitor },
    ],
  },
  specialized: {
    id: "specialized",
    title: "Specialized",
    label: "Project-based",
    pricingHow: "scoped project pricing",
    color: "#A855F7",
    icon: Palette,
    description: "Bespoke solutions and custom projects",
    items: [
      { name: "Bespoke UI/UX", pricing: "scoped project", icon: Palette },
      { name: "Marketing", pricing: "scoped project", icon: Megaphone },
      { name: "Custom Projects", pricing: "scoped project", icon: Wrench },
      { name: "Innovation Work", pricing: "scoped project", icon: Lightbulb },
    ],
  },
};

const SlideCommercialModel = () => {
  const { isDark } = useTheme();
  const [activeLayer, setActiveLayer] = useState(null);

  const layers = ["core", "platform", "specialized"];

  return (
    <div>
      <div className="k-eyebrow" style={{ color: "var(--k-accent)" }}>Commercial model</div>
      <h2 className={`k-h2 mt-3 max-w-[16ch] ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
        Built for
        <br />
        <span style={{ color: "var(--k-accent)" }}>modular adoption.</span>
      </h2>
      <p className={`k-lead mt-6 max-w-[54ch] ${isDark ? "text-white/70" : "text-black/60"}`}>
        Start with core CSP services. Add intelligence, software and bespoke
        layers as your strategy evolves. Every commercial term stays visible.
      </p>

      <div className="mt-8 grid grid-cols-3 gap-6">
        {layers.map((layerId) => {
          const layer = commercialLayers[layerId];
          const LayerIcon = layer.icon;
          const isActive = activeLayer === layerId;

          return (
            <button
              key={layerId}
              type="button"
              data-testid={`layer-${layerId}`}
              onClick={() => setActiveLayer(isActive ? null : layerId)}
              className={`text-left rounded-2xl p-6 border transition-all flex flex-col ${
                isDark ? "bg-white/[0.03] border-white/10 hover:bg-white/[0.05]" : "bg-white border-black/10 hover:border-black/25"
              } ${isActive ? "ring-1" : ""}`}
              style={isActive ? { borderColor: layer.color, boxShadow: `0 12px 40px -20px ${layer.color}` } : undefined}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${layer.color}20` }}>
                  <LayerIcon className="w-5 h-5" style={{ color: layer.color }} />
                </div>
                <div className="k-eyebrow" style={{ color: layer.color }}>
                  {layer.label}
                </div>
              </div>
              <div className={`mt-1 text-[14px] ${isDark ? "text-white/60" : "text-black/55"}`}>{layer.description}</div>

              <ul className="mt-5 space-y-2">
                {layer.items.map((item, i) => {
                  const ItemIcon = item.icon;
                  return (
                    <li
                      key={i}
                      className={`rounded-xl px-3 py-2.5 flex items-center gap-3 ${
                        isDark ? "bg-white/[0.03]" : "bg-black/[0.03]"
                      }`}
                    >
                      <ItemIcon className="w-4 h-4 flex-shrink-0" style={{ color: layer.color }} />
                      <span className={`text-[14px] font-medium flex-1 ${isDark ? "text-white/85" : "text-[#1d1d1f]"}`}>{item.name}</span>
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: `${layer.color}20`,
                          color: layer.color,
                        }}
                      >
                        {item.pricing}
                      </span>
                    </li>
                  );
                })}
              </ul>

              <div className={`mt-6 pt-4 border-t flex items-center gap-2 ${isDark ? "border-white/10" : "border-black/10"}`}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: layer.color }} />
                <span className="text-[12px] font-semibold" style={{ color: layer.color }}>{layer.pricingHow}</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className={`mt-12 pt-8 border-t flex items-center justify-between gap-4 flex-wrap ${isDark ? "border-white/10" : "border-black/10"}`}>
        <div className="flex items-center gap-2 text-[13px]">
          <span className="font-semibold" style={{ color: "#F2A900" }}>Core CSP</span>
          <ArrowRight className={`w-3.5 h-3.5 ${isDark ? "text-white/40" : "text-black/40"}`} />
          <span className="font-semibold" style={{ color: "#3B82F6" }}>Platform enablement</span>
          <ArrowRight className={`w-3.5 h-3.5 ${isDark ? "text-white/40" : "text-black/40"}`} />
          <span className="font-semibold" style={{ color: "#A855F7" }}>Experience differentiation</span>
        </div>
        <p className={`text-[13px] italic max-w-[36ch] ${isDark ? "text-white/45" : "text-black/45"}`}>
          Aeroplay scales commercially with your ambition — from traditional CSP to a full intelligent passenger experience.
        </p>
      </div>
    </div>
  );
};

export default SlideCommercialModel;
